import { safeText, fmtDate } from "./textHelpers";
import { LOGO_BASE64_PNG } from "../../assets/logoBase64";

// ============================================================
// PDF LAYOUT PRIMITIVES (shared by both report builders)
// ============================================================
// A4, millimeters — the standard international business-document format.
// Every text draw call routes through safeText() so nothing outside
// jsPDF's supported WinAnsi character set (arrows, smart quotes, emoji)
// ever reaches the page.

export const PAGE = { w: 210, h: 297, margin: 18 };
export const CONTENT_W = PAGE.w - PAGE.margin * 2;

export const COLORS = {
  brand: [37, 99, 235],       // blue-600
  brandDark: [30, 58, 138],   // blue-900
  text: [17, 24, 39],         // gray-900
  subtext: [107, 114, 128],   // gray-500
  border: [229, 231, 235],    // gray-200
  panel: [249, 250, 251],     // gray-50
  positive: [22, 163, 74],    // green-600
  negative: [220, 38, 38],    // red-600
  neutral: [107, 114, 128],
  chartPalette: [
    [37, 99, 235], [139, 92, 246], [16, 185, 129],
    [245, 158, 11], [236, 72, 153], [6, 182, 212],
  ],
};

export function directionColor(direction) {
  if (direction === "increased") return COLORS.positive;
  if (direction === "decreased") return COLORS.negative;
  return COLORS.neutral;
}

// ---- Text ----

export function text(doc, str, x, y, opts = {}) {
  doc.text(safeText(str), x, y, opts);
}

// Wraps text and returns the Y position after the last line.
export function wrapText(doc, str, x, y, maxWidth, lineHeight = 5.2) {
  const lines = doc.splitTextToSize(safeText(str), maxWidth);
  doc.text(lines, x, y);
  return y + lines.length * lineHeight;
}

// ---- Page chrome ----

export function drawCoverBranding(doc) {
  doc.setFillColor(...COLORS.brand);
  doc.rect(0, 0, PAGE.w, 4, "F");
}

export function drawHeader(doc, reportTitle, datasetName, { watermark = true } = {}) {
  doc.setFillColor(...COLORS.brand);
  doc.rect(0, 0, PAGE.w, 16, "F");

  doc.setTextColor(255, 255, 255);

  let brandTextX = PAGE.margin;

  if (watermark) {
    // Official brand logo — only shown on branded (free-plan) exports, same
    // gate as the "InsightPulse AI" text it sits beside. Premium exports
    // stay clean/unbranded, matching the existing watermark rule.
    const logoSize = 9;
    const logoY = (16 - logoSize) / 2;
    try {
      doc.addImage(LOGO_BASE64_PNG, "PNG", PAGE.margin, logoY, logoSize, logoSize);
      brandTextX = PAGE.margin + logoSize + 3;
    } catch (err) {
      // If image embedding ever fails, fall back to text-only branding
      // rather than breaking report generation.
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    text(doc, "InsightPulse AI", brandTextX, 10.5);
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const right = `${reportTitle} - ${datasetName}`;
  const w = doc.getTextWidth(safeText(right));
  const rightX = watermark ? PAGE.w - PAGE.margin - w : PAGE.margin;
  text(doc, right, watermark ? rightX : PAGE.margin, 10.5);

  doc.setTextColor(...COLORS.text);
}

export function drawFooters(doc, reportTitle, { watermark = true } = {}) {

  const pageCount = doc.internal.getNumberOfPages();

  for (let i = 1; i <= pageCount; i++) {

    doc.setPage(i);

    doc.setDrawColor(...COLORS.border);
    doc.line(PAGE.margin, PAGE.h - 14, PAGE.w - PAGE.margin, PAGE.h - 14);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.subtext);

    text(doc, watermark ? `${reportTitle} - Generated by InsightPulse AI` : reportTitle, PAGE.margin, PAGE.h - 9);

    const pageLabel = `Page ${i} of ${pageCount}`;
    const w = doc.getTextWidth(pageLabel);
    text(doc, pageLabel, PAGE.w - PAGE.margin - w, PAGE.h - 9);

    doc.setTextColor(...COLORS.text);
  }
}

export function sectionHeading(doc, y, label) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...COLORS.text);
  text(doc, label, PAGE.margin, y);

  doc.setDrawColor(...COLORS.brand);
  doc.setLineWidth(0.6);
  doc.line(PAGE.margin, y + 2, PAGE.margin + 22, y + 2);
  doc.setLineWidth(0.2);

  return y + 10;
}

export function checkPageBreak(doc, y, needed, onNewPage) {
  if (y + needed > PAGE.h - 20) {
    doc.addPage();
    onNewPage?.();
    return 24;
  }
  return y;
}

// ---- KPI cards ----
// cards: [{ label, value, sublabel, direction? }]
export function drawKpiGrid(doc, y, cards, perRow = 3) {

  const gap = 5;
  const cardW = (CONTENT_W - gap * (perRow - 1)) / perRow;
  const cardH = 24;

  cards.forEach((card, i) => {

    const col = i % perRow;
    const row = Math.floor(i / perRow);
    const x = PAGE.margin + col * (cardW + gap);
    const cy = y + row * (cardH + gap);

    doc.setFillColor(...COLORS.panel);
    doc.setDrawColor(...COLORS.border);
    doc.roundedRect(x, cy, cardW, cardH, 2, 2, "FD");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...COLORS.subtext);
    text(doc, card.label.toUpperCase(), x + 4, cy + 6);

    const valueColor = card.direction ? directionColor(card.direction) : COLORS.text;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(...valueColor);
    text(doc, card.value, x + 4, cy + 15);

    if (card.sublabel) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...COLORS.subtext);
      text(doc, card.sublabel, x + 4, cy + 20.5);
    }

    doc.setTextColor(...COLORS.text);
  });

  const rows = Math.ceil(cards.length / perRow);
  return y + rows * (cardH + gap);
}

// ---- Vector charts (no DOM/canvas dependency — drawn directly) ----

// Vertical column chart, e.g. year-over-year totals.
export function drawColumnChart(doc, x, y, w, h, data, { labelKey = "label", valueKey = "value" } = {}) {

  if (!data?.length) return y + h;

  const max = Math.max(...data.map(d => d[valueKey] || 0), 1);
  const gap = 6;
  const barW = (w - gap * (data.length - 1)) / data.length;
  const chartBottom = y + h;

  doc.setDrawColor(...COLORS.border);
  doc.line(x, chartBottom, x + w, chartBottom);

  data.forEach((d, i) => {

    const barH = Math.max((d[valueKey] / max) * (h - 12), 1);
    const bx = x + i * (barW + gap);
    const by = chartBottom - barH;

    doc.setFillColor(...COLORS.chartPalette[i % COLORS.chartPalette.length]);
    doc.roundedRect(bx, by, barW, barH, 1, 1, "F");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...COLORS.text);
    const label = safeText(String(d[labelKey]));
    const lw = doc.getTextWidth(label);
    text(doc, label, bx + barW / 2 - lw / 2, chartBottom + 5);
  });

  return chartBottom + 9;
}

// Horizontal bar chart, e.g. ranked representative performance.
export function drawHorizontalBarChart(doc, x, y, w, h, data, { labelKey = "label", valueKey = "value" } = {}) {

  if (!data?.length) return y + h;

  const max = Math.max(...data.map(d => d[valueKey] || 0), 1);
  const rowH = h / data.length;
  const labelW = 32;
  const barAreaW = w - labelW - 4;

  data.forEach((d, i) => {

    const ry = y + i * rowH;
    const barW = Math.max((d[valueKey] / max) * barAreaW, 1);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.text);
    const label = safeText(String(d[labelKey]));
    const lines = doc.splitTextToSize(label, labelW - 2);
    text(doc, lines[0], x, ry + rowH / 2 + 1);

    doc.setFillColor(...COLORS.chartPalette[i % COLORS.chartPalette.length]);
    doc.roundedRect(x + labelW, ry + rowH * 0.2, barW, rowH * 0.6, 1, 1, "F");
  });

  return y + h + 4;
}

// Simple line chart for time trends.
export function drawLineChart(doc, x, y, w, h, series, { labelKey = "period", valueKey = "value" } = {}) {

  if (!series?.length) return y + h;

  const values = series.map(d => d[valueKey] ?? 0);
  const max = Math.max(...values);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const chartBottom = y + h;
  const stepX = series.length > 1 ? w / (series.length - 1) : 0;

  doc.setDrawColor(...COLORS.border);
  doc.line(x, chartBottom, x + w, chartBottom);

  const points = series.map((d, i) => ({
    x: x + i * stepX,
    y: chartBottom - ((d[valueKey] - min) / range) * (h - 10),
  }));

  doc.setDrawColor(...COLORS.brand);
  doc.setLineWidth(0.8);

  for (let i = 0; i < points.length - 1; i++) {
    doc.line(points[i].x, points[i].y, points[i + 1].x, points[i + 1].y);
  }

  doc.setLineWidth(0.2);

  points.forEach((p, i) => {
    doc.setFillColor(...COLORS.brand);
    doc.circle(p.x, p.y, 1.3, "F");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...COLORS.text);
    const label = safeText(String(series[i][labelKey]));
    const lw = doc.getTextWidth(label);
    text(doc, label, p.x - lw / 2, chartBottom + 5);
  });

  return chartBottom + 9;
}

export function reportFileName(reportLabel, datasetName) {
  const clean = (datasetName || "dataset").replace(/\.[^/.]+$/, "").replace(/[^a-z0-9]+/gi, "_");
  const date = new Date().toISOString().slice(0, 10);
  return `${clean}_${reportLabel}_${date}.pdf`;
}

export { safeText, fmtDate };
